import { IconButton, Stack } from '@mui/material';
import { MedicalNeedFilter, TextOperator } from '@schooly/api';
import { FormTextField } from '@schooly/components/form-text-field';
import { DeleteIcon, PlusIcon, SimpleButton } from '@schooly/style';
import { FC } from 'react';
import { useIntl } from 'react-intl';

import { SimpleDropdownSelect } from '../';

const YES_NO_ANY_OPTIONS = [
  { nameTextId: 'filter-any', value: null },
  { nameTextId: 'yes', value: true },
  { nameTextId: 'no', value: false },
];

const TEXT_OPERATOR_OPTIONS = [
  { nameTextId: 'filter-contains', value: TextOperator.Contains },
  { nameTextId: 'filter-None', value: TextOperator.None },
  { nameTextId: 'filter-All', value: TextOperator.All },
];

type YesNoAnySelectProps = {
  value: boolean | null;
  onUpdate: (value: boolean | null) => void;
  placeholder?: string;
};

const YesNoAnySelect: FC<YesNoAnySelectProps> = ({ value, onUpdate, placeholder }) => {
  return (
    <SimpleDropdownSelect
      value={YES_NO_ANY_OPTIONS.find((option) => option.value === value)?.value}
      options={YES_NO_ANY_OPTIONS}
      onChange={onUpdate}
      placeholder={placeholder}
    />
  );
};

type OperatorSelectProps<T> = {
  operator: T;
  onUpdate: (operator: T) => void;
  options: { nameTextId: string; value: T }[];
};

const OperatorSelect = <T extends string>({
  operator,
  onUpdate,
  options,
}: OperatorSelectProps<T>) => {
  const { formatMessage } = useIntl();

  return (
    <SimpleDropdownSelect
      options={options}
      value={options.find((option) => option.value === operator)?.value}
      onChange={(v) => {
        const value = options.find((option) => option.value === v)?.value;
        if (!value) return;
        onUpdate(value);
      }}
      placeholder={formatMessage({ id: 'filter-show' })}
    />
  );
};

const MedicalNeedFilterComponent: FC<{
  medicalNeed: MedicalNeedFilter;
  onClear: () => void;
  onUpdate: (medicalNeed: MedicalNeedFilter) => void;
}> = ({ medicalNeed, onClear, onUpdate }) => {
  const { formatMessage } = useIntl();

  const renderConditionsSelect = () => {
    switch (medicalNeed.operator) {
      case TextOperator.All:
      case TextOperator.Contains:
        return (
          <Stack flexDirection="row" gap={1}>
            <Stack flex={1}>
              <YesNoAnySelect
                value={medicalNeed.severity ?? null}
                onUpdate={(severity) =>
                  onUpdate({ ...medicalNeed, severity: severity ?? undefined })
                }
                placeholder={formatMessage({ id: 'filter-severity' })}
              />
            </Stack>
            <Stack flex={1}>
              <YesNoAnySelect
                value={medicalNeed.reviewed_by_nurse ?? null}
                onUpdate={(reviewed_by_nurse) =>
                  onUpdate({ ...medicalNeed, reviewed_by_nurse: reviewed_by_nurse ?? undefined })
                }
                placeholder={formatMessage({ id: 'filter-reviewedByNurse' })}
              />
            </Stack>
          </Stack>
        );
      case TextOperator.None:
        return null;
      default: {
        const _: never = medicalNeed;
        return _;
      }
    }
  };

  return (
    <Stack flexDirection="row" gap={2} alignItems="flex-start">
      <Stack flex={1} gap={1}>
        <OperatorSelect
          options={TEXT_OPERATOR_OPTIONS}
          operator={medicalNeed.operator}
          onUpdate={(operator) => {
            if (operator === medicalNeed.operator) return;
            switch (operator) {
              case TextOperator.All:
                onUpdate({ ...medicalNeed, value: undefined, operator });
                break;
              case TextOperator.Contains:
                onUpdate({ ...medicalNeed, value: '', operator });
                break;
              case TextOperator.None:
                onUpdate({
                  ...medicalNeed,
                  value: undefined,
                  severity: undefined,
                  reviewed_by_nurse: undefined,
                  operator,
                });
                break;
              default: {
                const _: never = operator;
                return _;
              }
            }
          }}
        />
        {medicalNeed.operator === TextOperator.Contains && (
          <FormTextField
            value={medicalNeed.value}
            onChange={(e) => onUpdate({ ...medicalNeed, value: e.target.value })}
            placeholder={formatMessage({ id: 'filter-Text' })}
          />
        )}
        {renderConditionsSelect()}
      </Stack>
      <Stack pt={1.25}>
        <IconButton inverse onClick={onClear}>
          <DeleteIcon />
        </IconButton>
      </Stack>
    </Stack>
  );
};

type MedicalNeedFiltersProps = {
  filter: MedicalNeedFilter | undefined;
  onUpdate: (medicalNeeds: MedicalNeedFilter | null) => void;
};

export const MedicalNeedFilters: FC<MedicalNeedFiltersProps> = ({ filter, onUpdate }) => {
  const { formatMessage } = useIntl();

  return filter ? (
    <MedicalNeedFilterComponent
      medicalNeed={filter}
      onClear={() => onUpdate(null)}
      onUpdate={onUpdate}
    />
  ) : (
    <Stack alignSelf="flex-start">
      <SimpleButton
        startIcon={<PlusIcon />}
        onClick={() =>
          onUpdate({
            operator: TextOperator.All,
            value: undefined,
            severity: undefined,
            reviewed_by_nurse: undefined,
          })
        }
      >
        {formatMessage({ id: 'filter-addFilter' })}
      </SimpleButton>
    </Stack>
  );
};
